import React from 'react';
import {
  cancelTransfer,
  createTransferReturnLeg,
  shipTransfer,
  updateTransferDraft,
  TransferActionError,
  type TransferDraftPatch,
  type TransferRecord,
} from '@/data/transferRecord';
import { pluralize } from '@/domain/warehouse';
import TransferRecordDialog from './TransferRecordDialog';
import TransferShipmentFields, {
  EMPTY_SHIPMENT,
  shipmentDraftIsEmpty,
  shipmentInputFrom,
  type ShipmentDraft,
} from './TransferShipmentFields';
import {
  fromSiteInputValue,
  shortSiteName,
  siteName,
  siteZoneAbbreviation,
  toSiteInputValue,
} from './transferRecordView';
import type { TransferActionRunner } from './useTransferRecord';

type LifecycleDialogProps = {
  record: TransferRecord;
  runner: TransferActionRunner;
  onClose: () => void;
  onDone: (message: string) => void;
};

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Ship: everything picked, or (while picking is unfinished) what is picked, after the explicit
 * "Ship N of M" confirmation. How it travels is optional here and can be added later.
 */
export function TransferShipDialog({ record, runner, onClose, onDone }: LifecycleDialogProps) {
  const { transfer, totals } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const [draft, setDraft] = React.useState<ShipmentDraft>(EMPTY_SHIPMENT);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const picked = totals.picked;
  const ordered = totals.units;
  const short = picked < ordered;
  const from = siteName(transfer.fromSite, 'the shipping site');
  const to = siteName(transfer.toSite, 'the receiving site');

  const handleChange = React.useCallback((next: ShipmentDraft) => {
    setDraft(next);
  }, []);

  async function ship() {
    if (picked <= 0) {
      setError('Nothing is picked yet. Pick the units first.');
      return;
    }
    const payload = {
      shipShort: short,
      shipment: shipmentDraftIsEmpty(draft) ? null : shipmentInputFrom(draft, { withTimes: false, fromZone, toZone }),
    };
    setSaving(true);
    setError(null);
    try {
      const result = await runner.run(
        'ship',
        payload,
        (requestKey) => shipTransfer(transfer.id, requestKey, payload),
        { movesStock: true, fallback: 'The transfer was not shipped.' },
      );
      const shippedUnits = Number(result.raw.shipped_units);
      onDone(`Shipped ${pluralize(Number.isFinite(shippedUnits) && shippedUnits > 0 ? shippedUnits : picked, 'unit')}`);
    } catch (caught) {
      const refusedForShort = caught instanceof TransferActionError && caught.key === 'transfer_ship_requires_full_pick';
      setError(refusedForShort
        ? `${messageOf(caught, 'Some lines are not fully picked.')} The counts above are now up to date; press Ship again to send what is picked.`
        : messageOf(caught, 'The transfer was not shipped.'));
    } finally {
      setSaving(false);
    }
  }

  const label = short ? `Ship ${picked} of ${ordered}` : `Ship ${pluralize(picked, 'unit')}`;
  return (
    <TransferRecordDialog
      title={`Ship ${transfer.docNo ?? 'this transfer'}`}
      eyebrow={`Shipping from ${from}`}
      subtitle={`To ${to}`}
      onClose={onClose}
      busy={saving}
      error={error}
      errorTitle="Nothing was shipped"
      onSubmit={() => { void ship(); }}
      size="lg"
      footer={(
        <button type="submit" className="btn btn-primary" disabled={saving || picked <= 0}>
          {saving ? 'Shipping…' : label}
        </button>
      )}
    >
      {short ? (
        <div className="transfer-record-callout" data-tone="warn" role="note">
          <strong>{picked} of {pluralize(ordered, 'unit')} are picked.</strong>{' '}
          Shipping now sends the {pluralize(picked, 'picked unit')} and records the other {ordered - picked} as short on
          their lines. Lines with nothing picked are dropped from this transfer.
        </div>
      ) : (
        <p className="transfer-record-dialog__lead">
          All {pluralize(picked, 'unit')} on {pluralize(totals.lineCount, 'line')} are picked. Shipping moves them out of{' '}
          {from} and starts the trip to {shortSiteName(transfer.toSite?.name)}.
        </p>
      )}
      <p className="transfer-record-field__hint">
        Add how it travels so {to} knows what to expect. You can also add it later under Route &amp; shipments.
      </p>
      <TransferShipmentFields
        value={draft}
        onChange={handleChange}
        fromSiteId={transfer.fromSite?.id ?? null}
        fromZone={fromZone}
        toZone={toZone}
        withTimes={false}
        disabled={saving}
      />
    </TransferRecordDialog>
  );
}

/** Cancel before anything ships; a draft return leg goes with it. */
export function TransferCancelDialog({ record, runner, onClose, onDone }: LifecycleDialogProps) {
  const { transfer, totals } = record;
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const reasonId = React.useId();
  const leg = transfer.returnLeg;

  function handleReason(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setReason(event.target.value);
  }

  async function cancel() {
    const note = reason.trim() || null;
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        'cancel',
        note,
        (requestKey) => cancelTransfer(transfer.id, requestKey, note),
        { movesStock: true, fallback: 'The transfer was not cancelled.' },
      );
      onDone('Transfer cancelled');
    } catch (caught) {
      setError(messageOf(caught, 'The transfer was not cancelled.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <TransferRecordDialog
      title={`Cancel ${transfer.docNo ?? 'this transfer'}?`}
      eyebrow="Cancel transfer"
      onClose={onClose}
      busy={saving}
      error={error}
      errorTitle="The transfer was not cancelled"
      onSubmit={() => { void cancel(); }}
      footer={(
        <button type="submit" className="btn danger" disabled={saving}>
          {saving ? 'Cancelling…' : 'Cancel transfer'}
        </button>
      )}
    >
      <p className="transfer-record-dialog__lead">
        {totals.picked > 0
          ? `Nothing has shipped, so the ${pluralize(totals.picked, 'picked unit')} stay at ${siteName(transfer.fromSite, 'the shipping site')}. History keeps the picks.`
          : 'Nothing has been picked or shipped. The transfer stays in History as cancelled.'}
        {leg && leg.status === 'draft' ? ` Its draft return leg ${leg.docNo ?? ''} is cancelled too.` : ''}
      </p>
      <div className="transfer-record-field">
        <label className="transfer-record-field__label" htmlFor={reasonId}>Reason</label>
        <textarea
          id={reasonId}
          className="transfer-record-field__control transfer-record-field__control--text"
          value={reason}
          onChange={handleReason}
          rows={3}
          placeholder="Optional; shown in History"
          maxLength={1000}
          data-autofocus="true"
        />
      </div>
    </TransferRecordDialog>
  );
}

type DateKey = 'need_by_at' | 'expected_ship_at' | 'expected_receive_at' | 'expected_return_at';

/** Change the dates before picking starts. Each date is on the clock of the site it happens at. */
export function TransferDatesDialog({ record, runner, onClose, onDone }: LifecycleDialogProps) {
  const { transfer } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const temporary = transfer.scope === 'temporary';
  const fields = React.useMemo(() => {
    const list: Array<{ key: DateKey; label: string; zone: string | null; site: string; value: string | null }> = [
      { key: 'expected_ship_at', label: 'Ships', zone: fromZone, site: shortSiteName(transfer.fromSite?.name), value: transfer.expectedShipAt },
      { key: 'expected_receive_at', label: 'Arrives', zone: toZone, site: shortSiteName(transfer.toSite?.name), value: transfer.expectedReceiveAt },
      { key: 'need_by_at', label: 'Needed by', zone: toZone, site: shortSiteName(transfer.toSite?.name), value: transfer.needByAt },
    ];
    if (temporary) {
      list.push({ key: 'expected_return_at', label: 'Comes back', zone: toZone, site: shortSiteName(transfer.toSite?.name), value: transfer.expectedReturnAt });
    }
    return list;
  }, [fromZone, temporary, toZone, transfer.expectedReceiveAt, transfer.expectedReturnAt, transfer.expectedShipAt, transfer.fromSite?.name, transfer.needByAt, transfer.toSite?.name]);
  const [values, setValues] = React.useState<{ [key: string]: string }>(() => Object.fromEntries(
    fields.map((field) => [field.key, toSiteInputValue(field.value, field.zone)]),
  ));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const baseId = React.useId();

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setError(null);
  }

  async function save() {
    const patch: TransferDraftPatch = {};
    const iso: { [key: string]: string | null } = {};
    for (const field of fields) {
      const raw = values[field.key] ?? '';
      const next = raw ? fromSiteInputValue(raw, field.zone) : null;
      iso[field.key] = next;
      const before = field.value ? toSiteInputValue(field.value, field.zone) : '';
      if (raw !== before) patch[field.key] = next;
    }
    const ships = iso.expected_ship_at ?? null;
    const arrives = iso.expected_receive_at ?? null;
    const back = iso.expected_return_at ?? null;
    if (ships && arrives && Date.parse(arrives) < Date.parse(ships)) {
      setError('It cannot arrive before it ships.');
      return;
    }
    if (back && arrives && Date.parse(back) < Date.parse(arrives)) {
      setError('It cannot come back before it arrives.');
      return;
    }
    if (!Object.keys(patch).length) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        'dates',
        patch,
        (requestKey) => updateTransferDraft(transfer.id, requestKey, patch),
        { movesStock: true, fallback: 'The dates were not saved.' },
      );
      onDone('Dates saved');
    } catch (caught) {
      setError(messageOf(caught, 'The dates were not saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <TransferRecordDialog
      title="Change dates"
      eyebrow={transfer.docNo ?? 'Transfer'}
      subtitle="Dates can change until picking starts."
      onClose={onClose}
      busy={saving}
      error={error}
      onSubmit={() => { void save(); }}
      footer={(
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save dates'}
        </button>
      )}
    >
      <div className="transfer-record-form-grid transfer-record-form-grid--two">
        {fields.map((field, index) => {
          const abbreviation = siteZoneAbbreviation(field.zone);
          const id = `${baseId}-${field.key}`;
          return (
            <div key={field.key} className="transfer-record-field">
              <label className="transfer-record-field__label" htmlFor={id}>
                {field.label} <span className="transfer-record-field__zone">{field.site}{abbreviation ? ` · ${abbreviation}` : ''}</span>
              </label>
              <input
                id={id}
                name={field.key}
                className="transfer-record-field__control"
                type="datetime-local"
                value={values[field.key] ?? ''}
                onChange={handleChange}
                data-autofocus={index === 0 ? 'true' : undefined}
              />
            </div>
          );
        })}
      </div>
    </TransferRecordDialog>
  );
}

/** Make the return leg now (a temporary transfer gets one when confirmed; older ones may not have it). */
export function TransferReturnLegDialog({ record, runner, onClose, onDone }: LifecycleDialogProps) {
  const { transfer } = record;
  const toZone = transfer.toSite?.timezone ?? null;
  const [shipAt, setShipAt] = React.useState(() => toSiteInputValue(transfer.expectedReturnAt, toZone));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const shipId = React.useId();
  const abbreviation = siteZoneAbbreviation(toZone);

  function handleShipAt(event: React.ChangeEvent<HTMLInputElement>) {
    setShipAt(event.target.value);
    setError(null);
  }

  async function create() {
    const iso = shipAt ? fromSiteInputValue(shipAt, toZone) : null;
    setSaving(true);
    setError(null);
    try {
      const result = await runner.run(
        'return-leg',
        iso,
        (requestKey) => createTransferReturnLeg(transfer.id, requestKey, iso),
        { movesStock: true, fallback: 'The return leg was not created.' },
      );
      onDone(result.returnLeg?.docNo ? `Return leg ${result.returnLeg.docNo} is ready` : 'Return leg created');
    } catch (caught) {
      setError(messageOf(caught, 'The return leg was not created.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <TransferRecordDialog
      title="Create the return leg"
      eyebrow={transfer.docNo ?? 'Transfer'}
      subtitle={`${siteName(transfer.toSite, 'The receiving site')} back to ${siteName(transfer.fromSite, 'the shipping site')}`}
      onClose={onClose}
      busy={saving}
      error={error}
      onSubmit={() => { void create(); }}
      footer={(
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Creating…' : 'Create return leg'}
        </button>
      )}
    >
      <p className="transfer-record-dialog__lead">
        A draft transfer back to {siteName(transfer.fromSite, 'the shipping site')}. Its lines copy what is received
        here, less anything kept, and it is confirmed and shipped like any other transfer.
      </p>
      <div className="transfer-record-field transfer-record-field--narrow-date">
        <label className="transfer-record-field__label" htmlFor={shipId}>
          Ships back{abbreviation ? ` (${abbreviation})` : ''}
        </label>
        <input
          id={shipId}
          className="transfer-record-field__control"
          type="datetime-local"
          value={shipAt}
          onChange={handleShipAt}
          data-autofocus="true"
        />
      </div>
    </TransferRecordDialog>
  );
}
